import { Fragment, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/header/Header';
import Videos from '../../components/Videos/Videos';
import './KitsPage.css';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ScheduleIcon from '@mui/icons-material/Schedule';
import OpacityIcon from '@mui/icons-material/Opacity';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import FingerprintOutlinedIcon from '@mui/icons-material/FingerprintOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import hlaBox from '../../images/kits/hla-box.png';
import kirBox from '../../images/kits/kir-box.png';
import chimHsBox from '../../images/kits/chimerism-hs-box.png';
import chimMdBox from '../../images/kits/chimerism-md-box.png';
import mhcLohBox from '../../images/kits/mhc-loh-box.png';
import fcrBox from '../../images/kits/fcr-box.png';

// The earlier brochure-style kit panels, now reached through each kit's
// "Kit Brochure" link rather than shown beside the copy.
import hlaKit from '../../images/kits/hla-kit.png';
import kirKit from '../../images/kits/kir-kit.png';
import chimHsKit from '../../images/kits/chimerism-hs-kit.png';
import chimMdKit from '../../images/kits/chimerism-md-kit.png';
import fcrKit from '../../images/kits/fcr-kit.png';

import process1 from '../../images/kit-process/1-stage-1-pcr.png';
import process2 from '../../images/kit-process/2-stage-2-pcr.png';
import process3 from '../../images/kit-process/3-test-tubes.png';
import process4 from '../../images/kit-process/5-mgs.png';
import process5 from '../../images/kit-process/6-report.png';

import hla_brochure from '../../files/kits/brochures/hla-brochure.pdf';
import hla_insert from '../../files/kits/inserts/hla-insert.pdf';
import hla_protocol from '../../files/kits/protocols/hla-protocol.pdf';
import chim_brochure from '../../files/kits/brochures/chimerism-brochure.pdf';
import chim_insert from '../../files/kits/inserts/chimerism-insert.pdf';
import chim_protocol from '../../files/kits/protocols/chimerism-protocol.pdf';

/* A real link to the brochure image, so it can be opened in a new tab or
   copied, but a plain click shows it in the page's lightbox instead. */
const BrochureLink = ({ src, onOpen }) => (
  <a
    href={src}
    target='_blank'
    rel='noreferrer'
    className='kit-dl-link kit-brochure-link'
    onClick={(event) => {
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return;
      event.preventDefault();
      onOpen(src);
    }}
  >
    Kit Brochure <ImageOutlinedIcon />
  </a>
);

const KitsPage = ({ headers }) => {
  const [zoomedImg, setZoomedImg] = useState(null);
  return (
    <>
      <Helmet>
        <title>Scisco Genetics - Typing Kits for HLA Typing, KIR Typing, FcGR typing and Chimerism Testing</title>
        <meta
          name='description'
          content='Typing Kits for HLA, Chimerism, KIR and FcR-IGHG make Scisco Genetics technology available to any lab'
        />
      </Helmet>
      <Header header={headers.filter((header) => header.key === 'kits')} />

      {/* ── HERO ── */}
      <section className='kits-hero'>
        <div className='kits-hero-text'>
          <h1 className='kits-hero-title'>
            High-Resolution Genotyping.<br />
            <span className='kits-hero-accent'>Simplified. Scaled. Proven.</span>
          </h1>
          {/* The non-breaking space ties each paragraph's last two words
              together, so neither can be left alone on a closing line.
              .kits-hero-desc also sets text-wrap: pretty, which handles this
              and the rest of the ragging where supported; this is the part
              that holds regardless. */}
          <p className='kits-hero-desc'>
            Bring high-resolution immune genotyping into the lab with streamlined
            NGS kits designed for speed, accuracy, and real-world&nbsp;workflows.
          </p>
          <p className='kits-hero-desc'>
            From HLA and KIR to chimerism and Fc receptor profiling, ScisGo™ kits
            deliver phased, unambiguous results—without&nbsp;complexity.
          </p>
        </div>
      </section>

      {/* ── HLA KIT ── */}
      <section className='kit-section kit-section-alt' id='hla-kit'>
        <div className='kit-section-inner'>
          <span className='kit-badge'>ScisGo™ HLA v6 Kits</span>
          <div className='kit-content'>
            <div className='kit-text'>
              <h2 className='kit-main-headline'>Complete HLA Resolution<br /><em>In a Single Assay</em></h2>
              <p className='kit-desc'>
                Stop trading time for clarity. ScisGo™ HLA v6 delivers fully phased 3-field typing across all major loci in one streamlined workflow—so you get definitive answers, faster.
              </p>
              <div className='hla-class-grid'>
                <div className='hla-class-block'>
                  <span className='hla-class-label'>CLASS I</span>
                  <div className='hla-genes'>
                    <span>A</span><span>B</span><span>C</span>
                  </div>
                </div>
                <div className='hla-class-block'>
                  <span className='hla-class-label'>CLASS II</span>
                  <div className='hla-genes hla-genes-ii'>
                    <span>DRB</span><span>DQB</span><span>DPB</span>
                  </div>
                </div>
              </div>
              <BrochureLink src={hlaKit} onOpen={setZoomedImg} />
            </div>
            <img src={hlaBox} alt='HLA v6 kit' className='kit-img kit-img-clickable' onClick={() => setZoomedImg(hlaBox)} />
          </div>
          <div className='kit-features'>
            <div className='kit-feature'>
              <DoneAllIcon className='kit-feature-icon' />
              <span>All loci.<br />One assay.</span>
            </div>
            <div className='kit-feature'>
              <ScheduleIcon className='kit-feature-icon' />
              <span>From DNA to data in ~30 hours</span>
            </div>
            <div className='kit-feature'>
              <OpacityIcon className='kit-feature-icon' />
              <span>Low input.<br />High flexibility.</span>
            </div>
            <div className='kit-feature'>
              <VerifiedOutlinedIcon className='kit-feature-icon' />
              <span>Phased accuracy.<br />No ambiguity.</span>
            </div>
          </div>
          <div className='kit-downloads'>
            <a href={hla_brochure} target='_blank' rel='noreferrer' className='kit-dl-link'>HLA V6 Product Brochure <FileDownloadOutlinedIcon /></a>
            <a href={hla_insert} target='_blank' rel='noreferrer' className='kit-dl-link'>HLA V6 Product Insert <FileDownloadOutlinedIcon /></a>
            <a href={hla_protocol} target='_blank' rel='noreferrer' className='kit-dl-link'>HLA V6 Product Protocol <FileDownloadOutlinedIcon /></a>
          </div>
        </div>
      </section>

      {/* ── KIR KIT ── */}
      <section className='kit-section' id='kir-kit'>
        <div className='kit-section-inner'>
          <span className='kit-badge'>ScisGo™ KIR v6 Kits</span>
          <div className='kit-content'>
            <div className='kit-text'>
              <h2 className='kit-main-headline'>Explore KIR Diversity<br /><em>Gene to Haplotype</em></h2>
              <p className='kit-desc'>
                The KIR region resists simple genotyping—gene content varies between individuals, and structure carries the signal. ScisGo™ KIR v6 determines gene copy number, deduces haplotype structure against established reference haplotypes, and adds targeted allele typing, in one PCR-based NGS workflow.
              </p>
              <div className='kir-list'>
                <span className='kir-list-title'>Determines</span>
                <div className='kir-capability-grid'>
                  <span className='kir-capability'>Gene copy number</span>
                  <span className='kir-capability'>Haplotype structure</span>
                  <span className='kir-capability'>Allele typing, exons 1–8</span>
                  <span className='kir-capability'>IMGT/KIR reference mapping</span>
                </div>
              </div>
              <BrochureLink src={kirKit} onOpen={setZoomedImg} />
            </div>
            <img src={kirBox} alt='KIR v6 kit' className='kit-img kit-img-clickable' onClick={() => setZoomedImg(kirBox)} />
          </div>
          <div className='kit-features'>
            <div className='kit-feature'>
              <FingerprintOutlinedIcon className='kit-feature-icon' />
              <span>KIR profiling across the receptor gene family</span>
            </div>
            <div className='kit-feature'>
              <HubOutlinedIcon className='kit-feature-icon' />
              <span>Haplotype analysis from gene copy number</span>
            </div>
            <div className='kit-feature'>
              <ScienceOutlinedIcon className='kit-feature-icon' />
              <span>Sequencing-based KIR analysis</span>
            </div>
            <div className='kit-feature'>
              <BiotechOutlinedIcon className='kit-feature-icon' />
              <span>PCR-based library preparation</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── CHIMERISM HS KIT ── */}
      <section className='kit-section kit-section-alt' id='chimerism-hs-kit'>
        <div className='kit-section-inner'>
          <span className='kit-badge'>ScisGo™ Chimerism HS Kits</span>
          <div className='kit-content'>
            <div className='kit-text'>
              <h2 className='kit-main-headline'>Detect What Matters<br /><em>Down to 0.1%</em></h2>
              <p className='kit-desc'>
                Post-transplant monitoring demands sensitivity you can trust. ScisGo™ Chimerism HS resolves low-level donor and recipient mixtures down to 0.1%, from DNA input as low as 0.5 ng—roughly 100 cells—for earlier insight into engraftment and relapse risk.
              </p>
              <div className='chim-stat'>
                <div className='chim-stat-block'>
                  <span className='chim-stat-label'>DONOR</span>
                  <div className='chim-dot chim-dot-donor' />
                </div>
                <div className='chim-pct'>
                  <span className='chim-pct-num'>0.1%</span>
                  <span className='chim-pct-label'>MINOR POPULATION DETECTED</span>
                </div>
                <div className='chim-stat-block'>
                  <span className='chim-stat-label'>RECIPIENT</span>
                  <div className='chim-dot chim-dot-recipient' />
                </div>
              </div>
              <BrochureLink src={chimHsKit} onOpen={setZoomedImg} />
            </div>
            <img src={chimHsBox} alt='Chimerism HS kit' className='kit-img kit-img-clickable' onClick={() => setZoomedImg(chimHsBox)} />
          </div>
          <div className='kit-features'>
            <div className='kit-feature'>
              <TrendingDownIcon className='kit-feature-icon' />
              <span>Down to 0.1% sensitivity</span>
            </div>
            <div className='kit-feature'>
              <OpacityIcon className='kit-feature-icon' />
              <span>Input as low as 0.5 ng (~100 cells)</span>
            </div>
            <div className='kit-feature'>
              <SpeedOutlinedIcon className='kit-feature-icon' />
              <span>Fast turnaround ~12 hours DNA-to-data</span>
            </div>
            <div className='kit-feature'>
              <TuneOutlinedIcon className='kit-feature-icon' />
              <span>Capacity and sensitivity tuned to the study</span>
            </div>
          </div>
          <div className='kit-downloads'>
            <a href={chim_brochure} target='_blank' rel='noreferrer' className='kit-dl-link'>CHIM Product Brochure <FileDownloadOutlinedIcon /></a>
            <a href={chim_insert} target='_blank' rel='noreferrer' className='kit-dl-link'>CHIM Product Insert <FileDownloadOutlinedIcon /></a>
            <a href={chim_protocol} target='_blank' rel='noreferrer' className='kit-dl-link'>CHIM Product Protocol <FileDownloadOutlinedIcon /></a>
          </div>
        </div>
      </section>

      {/* ── CHIMERISM MD KIT ── */}
      <section className='kit-section' id='chimerism-md-kit'>
        <div className='kit-section-inner'>
          <span className='kit-badge'>ScisGo™ Chimerism MD Kits</span>
          <div className='kit-content'>
            <div className='kit-text'>
              <h2 className='kit-main-headline'>Resolve Every Contributor<br /><em>Up to Three Donors</em></h2>
              <p className='kit-desc'>
                Not every transplant has a single donor. ScisGo™ Chimerism MD distinguishes up to three independent donors within recipient DNA, sampling all chromosomes and reporting 95% confidence intervals alongside the sensitivity achieved for each result.
              </p>
              <div className='chim-stat'>
                <div className='chim-stat-block'>
                  <span className='chim-stat-label'>DONORS</span>
                  <div className='chim-dot-row'>
                    <div className='chim-dot chim-dot-donor' />
                    <div className='chim-dot chim-dot-donor' />
                    <div className='chim-dot chim-dot-donor' />
                  </div>
                </div>
                <div className='chim-pct'>
                  <span className='chim-pct-num'>3</span>
                  <span className='chim-pct-label'>INDEPENDENT DONORS RESOLVED</span>
                </div>
                <div className='chim-stat-block'>
                  <span className='chim-stat-label'>RECIPIENT</span>
                  <div className='chim-dot chim-dot-recipient' />
                </div>
              </div>
              <BrochureLink src={chimMdKit} onOpen={setZoomedImg} />
            </div>
            <img src={chimMdBox} alt='Chimerism MD kit' className='kit-img kit-img-clickable' onClick={() => setZoomedImg(chimMdBox)} />
          </div>
          <div className='kit-features'>
            <div className='kit-feature'>
              <GroupOutlinedIcon className='kit-feature-icon' />
              <span>Up to three independent donors</span>
            </div>
            <div className='kit-feature'>
              <DoneAllIcon className='kit-feature-icon' />
              <span>All chromosomes sampled</span>
            </div>
            <div className='kit-feature'>
              <AssessmentOutlinedIcon className='kit-feature-icon' />
              <span>95% confidence intervals reported</span>
            </div>
            <div className='kit-feature'>
              <TuneOutlinedIcon className='kit-feature-icon' />
              <span>Adjustable reactions and sample throughput</span>
            </div>
          </div>
          <div className='kit-downloads'>
            <a href={chim_brochure} target='_blank' rel='noreferrer' className='kit-dl-link'>CHIM Product Brochure <FileDownloadOutlinedIcon /></a>
            <a href={chim_insert} target='_blank' rel='noreferrer' className='kit-dl-link'>CHIM Product Insert <FileDownloadOutlinedIcon /></a>
            <a href={chim_protocol} target='_blank' rel='noreferrer' className='kit-dl-link'>CHIM Product Protocol <FileDownloadOutlinedIcon /></a>
          </div>
        </div>
      </section>

      {/* ── MHC-LOH KIT ── */}
      {/* Draft copy: the description needs product review before this goes
          live. It quotes no sensitivity, input or turnaround figures, since
          those aren't established or depend on the lab's sequencer and
          sequencing kit. There is no brochure panel or documentation for this
          kit yet, so it has no Kit Brochure link or downloads. */}
      <section className='kit-section kit-section-alt' id='mhc-loh-kit'>
        <div className='kit-section-inner'>
          <span className='kit-badge'>ScisGo™ MHC-LOH Kits</span>
          <div className='kit-content'>
            <div className='kit-text'>
              <h2 className='kit-main-headline'>Look Inside the MHC<br /><em>Where Relapse Can Hide</em></h2>
              <p className='kit-desc'>
                After HLA-mismatched transplant, leukemic cells can escape the donor immune response by losing the mismatched HLA haplotype—a change overall chimerism results do not reveal. ScisGo™ MHC-LOH measures chimerism across the MHC region to detect loss of heterozygosity on chromosome 6p.
              </p>
              <div className='chim-stat'>
                <div className='chim-stat-block'>
                  <span className='chim-stat-label'>RETAINED</span>
                  <div className='chim-dot chim-dot-recipient' />
                </div>
                <div className='chim-pct'>
                  <span className='chim-pct-num'>6p</span>
                  <span className='chim-pct-label'>HLA HAPLOTYPE LOSS DETECTED</span>
                </div>
                <div className='chim-stat-block'>
                  <span className='chim-stat-label'>LOST</span>
                  <div className='chim-dot chim-dot-lost' />
                </div>
              </div>
            </div>
            <img src={mhcLohBox} alt='MHC-LOH kit' className='kit-img kit-img-clickable' onClick={() => setZoomedImg(mhcLohBox)} />
          </div>
          <div className='kit-features'>
            <div className='kit-feature'>
              <ManageSearchIcon className='kit-feature-icon' />
              <span>Chimerism across the MHC on chromosome 6p</span>
            </div>
            <div className='kit-feature'>
              <BiotechOutlinedIcon className='kit-feature-icon' />
              <span>The same two-stage PCR workflow as every ScisGo™ kit</span>
            </div>
            <div className='kit-feature'>
              <StorageOutlinedIcon className='kit-feature-icon' />
              <span>24 samples per kit</span>
            </div>
            <div className='kit-feature'>
              <ScienceOutlinedIcon className='kit-feature-icon' />
              <span>Runs on standard NGS sequencers</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FC-FCR KIT ── */}
      <section className='kit-section' id='fcr-kit'>
        <div className='kit-section-inner'>
          <span className='kit-badge'>ScisGo™ FcR-IGHG Kits</span>
          <div className='kit-content'>
            <div className='kit-text'>
              <h2 className='kit-main-headline'>Decode Fc Biology<br /><em>at Full Resolution</em></h2>
              <p className='kit-desc'>
                Immune response is driven by interaction. ScisGo™ FcR-IGHG kits enable high-resolution genotyping of Fc receptor genes and IgG domains in a single integrated system—unlocking deeper insight into immune function and therapeutic response.
              </p>
              <div className='fcr-list'>
                <span className='fcr-list-title'>Fc Receptors</span>
                <div className='fcr-genes-grid'>
                  {['FCGR2A','FCGR2B','FCGR3A','FCGR3B','FCGR1B','FCERIG'].map(r => (
                    <span key={r} className='fcr-gene'>{r}</span>
                  ))}
                </div>
              </div>
              <BrochureLink src={fcrKit} onOpen={setZoomedImg} />
            </div>
            <img src={fcrBox} alt='FcR-IGHG kit' className='kit-img kit-img-clickable' onClick={() => setZoomedImg(fcrBox)} />
          </div>
          <div className='kit-features'>
            <div className='kit-feature'>
              <ManageSearchIcon className='kit-feature-icon' />
              <span>Comprehensive variant detection across FcR and IGHG</span>
            </div>
            <div className='kit-feature'>
              <AccountTreeOutlinedIcon className='kit-feature-icon' />
              <span>Phased domain resolution (CH1, CH2, CH3)</span>
            </div>
            <div className='kit-feature'>
              <StorageOutlinedIcon className='kit-feature-icon' />
              <span>High throughput — Up to 384 samples</span>
            </div>
            <div className='kit-feature'>
              <BiotechOutlinedIcon className='kit-feature-icon' />
              <span>PCR-only workflow. No robotics required.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── WORKFLOW ── */}
      <section className='kits-workflow' id='kit-workflow'>
        <div className='kits-workflow-inner'>
          <div className='workflow-layout'>
            <div className='workflow-text'>
              <span className='kit-badge'>ScisGo™ Workflow</span>
              <h2 className='kit-main-headline'>From Sample to Insight<br /><em>Without the Friction</em></h2>
              <p className='kit-desc'>
                Every ScisGo™ kit follows the same intuitive workflow, designed to minimize training time and maximize throughput. No complex instrumentation. No unnecessary hand-offs. Just a clean, repeatable path from DNA to actionable data.
              </p>
            </div>
          <div className='workflow-steps'>
            {[
              { num: '1', label: 'AMPLIFY', desc: 'Add DNA to pre-plated primers', img: process1 },
              { num: '2', label: 'ENHANCE', desc: 'Second PCR, no transfers required', img: process2 },
              { num: '3', label: 'PREPARE', desc: 'Pool, purify, and quantify', img: process3 },
              { num: '4', label: 'SEQUENCE', desc: 'Standard NGS workflow', img: process4 },
              { num: '5', label: 'ANALYZE', desc: 'Generate clear, actionable reports', img: process5 },
            ].map((step, i, arr) => (
              <Fragment key={step.num}>
                <div className='workflow-step'>
                  <div className='workflow-step-num'>{step.num}</div>
                  <div className='workflow-step-img-wrap'>
                    <img src={step.img} alt={step.label} className='workflow-step-img' />
                  </div>
                  <span className='workflow-step-label'>{step.label}</span>
                  <span className='workflow-step-desc'>{step.desc}</span>
                </div>
                {i < arr.length - 1 && <div className='workflow-connector' />}
              </Fragment>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* ── VIDEOS ── */}
      <Videos />

      {/* ── CTA ── */}
      <section className='kits-cta'>
        <div className='kits-cta-inner'>
          <h2 className='kits-cta-title'>Bring advanced immunogenetics<br />into everyday workflow.</h2>
          <p className='kits-cta-desc'>
            ScisGo™ kits combine NGS power, simplified protocols, and high-resolution output—so any lab can generate clinically meaningful insights with confidence.
          </p>
          <div className='kits-cta-buttons'>
            <a href='/contact#contact-form' className='kits-cta-btn kits-cta-btn-primary'>Request a Quote</a>
            <a href={hla_brochure} target='_blank' rel='noreferrer' className='kits-cta-btn kits-cta-btn-secondary'>Download Brochure</a>
          </div>
        </div>
      </section>

      {zoomedImg && (
        <div className='lightbox-overlay' onClick={() => setZoomedImg(null)}>
          <img src={zoomedImg} className='lightbox-img' alt='zoomed' />
        </div>
      )}
    </>
  );
};

export default KitsPage;
